import { Box } from '@mui/material';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router';
import { Button } from 'src/lib/components/Button';
import { Typography } from 'src/lib/components/Typography';
import Container from './components/Container';

const CALLBACK_CONFIG = {
    '/mobile-auth/facility/callback': {
        appName: 'ResQ Facility',
        scheme: 'resq-facility',
    },
    '/mobile-auth/vendor/callback': {
        appName: 'ResQ Partner',
        scheme: 'resq-partner',
    },
} as const;

const FORWARDED_PARAMS = ['code', 'error', 'wrongApp'] as const;

export const buildMobileAuthFallbackDeepLink = (
    pathname: string,
    search: string,
): string | null => {
    const config = CALLBACK_CONFIG[pathname as keyof typeof CALLBACK_CONFIG];
    if (!config) {
        return null;
    }

    const incoming = new URLSearchParams(search);
    const outgoing = new URLSearchParams();
    for (const key of FORWARDED_PARAMS) {
        const value = incoming.get(key);
        if (value) {
            outgoing.set(key, value);
        }
    }
    const query = outgoing.toString();
    if (!query) {
        return null;
    }

    return `${config.scheme}://auth/code-callback?${query}`;
};

export const MobileAuthCallbackPage = () => {
    const { t } = useTranslation('auth.mobileAuthCallback');
    const location = useLocation();
    const config = CALLBACK_CONFIG[location.pathname as keyof typeof CALLBACK_CONFIG];
    const deepLink = buildMobileAuthFallbackDeepLink(location.pathname, location.search);

    useEffect(() => {
        if (deepLink) {
            window.location.replace(deepLink);
        }
    }, [deepLink]);

    if (!config) {
        return null;
    }

    return (
        <Container width={421} isVendor={config.scheme === 'resq-partner'}>
            <Box textAlign="center">
                <Typography role="heading2">{t('title', { appName: config.appName })}</Typography>
                <Typography role="body" color="subdued" sx={{ marginBottom: 3, marginTop: 2 }}>
                    {deepLink ? t('description') : t('invalidDescription')}
                </Typography>
                {deepLink && (
                    <Button
                        kind="filled"
                        color="primary"
                        fullWidth
                        onClick={() => window.location.assign(deepLink)}
                    >
                        {t('openButton', { appName: config.appName })}
                    </Button>
                )}
            </Box>
        </Container>
    );
};
